import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, X, ArrowLeft, MapPin, CalendarDays, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { tInput, tLabel, tBtn, useTrackEvents, CsvImportButton } from "./trackUi";

export default function TrackMeets({ orgId, canWrite }) {
  const events = useTrackEvents();
  const [meets, setMeets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", location: "", season: "" });
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/track/meets?organization_id=${orgId}`); setMeets(data); }
    catch { toast.error("Failed to load meets."); } finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/track/meets", { ...form, organization_id: orgId }); toast.success("Meet created."); setForm({ name: "", date: "", location: "", season: "" }); setShowAdd(false); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (m) => { if (!window.confirm(`Delete ${m.name} and its results?`)) return; try { await api.delete(`/track/meets/${m.id}`); load(); } catch { toast.error("Delete failed."); } };

  if (open) return <MeetDetail meet={open} orgId={orgId} canWrite={canWrite} events={events} onBack={() => setOpen(null)} />;

  return (
    <div data-testid="track-meets">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div><h1 className="font-display font-black uppercase tracking-tighter text-4xl">Meets</h1><p className="text-white/40 text-sm mt-1" data-testid="meets-count">{meets.length} meets</p></div>
        {canWrite && <button data-testid="add-meet" onClick={() => setShowAdd((v) => !v)} className={tBtn}>{showAdd ? <X size={14} /> : <Plus size={14} />} {showAdd ? "Cancel" : "New Meet"}</button>}
      </div>
      {showAdd && (
        <form onSubmit={create} className="bg-[#121212] border border-white/10 p-6 mb-6 grid sm:grid-cols-4 gap-4" data-testid="meet-form">
          <div className="sm:col-span-2"><label className={tLabel}>Name *</label><input data-testid="meet-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={tInput} /></div>
          <div><label className={tLabel}>Date</label><input data-testid="meet-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={tInput} /></div>
          <div><label className={tLabel}>Season</label><input value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="2026 Outdoor" className={tInput} /></div>
          <div className="sm:col-span-2"><label className={tLabel}>Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={tInput} /></div>
          <div className="sm:col-span-4"><button data-testid="meet-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Meet</button></div>
        </form>
      )}
      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : meets.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid="meets-empty">No meets yet. {canWrite ? "Create one to start entering results." : ""}</div>
        : (
          <div className="space-y-2" data-testid="meets-list">
            {meets.map((m) => (
              <div key={m.id} data-testid={`meet-${m.id}`} className="bg-[#121212] border border-white/10 p-4 flex items-center justify-between gap-3 hover:border-white/25 transition-colors">
                <button onClick={() => setOpen(m)} data-testid={`open-meet-${m.id}`} className="flex-1 text-left min-w-0">
                  <p className="font-bold">{m.name}</p>
                  <p className="text-white/50 text-xs mt-0.5 flex flex-wrap gap-3">{m.date && <span className="flex items-center gap-1"><CalendarDays size={12} />{m.date}</span>}{m.location && <span className="flex items-center gap-1"><MapPin size={12} />{m.location}</span>}{m.season && <span>{m.season}</span>}</p>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  {canWrite && <button data-testid={`del-meet-${m.id}`} onClick={() => del(m)} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>}
                  <ChevronRight size={16} className="text-white/25" />
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

function MeetDetail({ meet, orgId, canWrite, events, onBack }) {
  const [athletes, setAthletes] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entry, setEntry] = useState({ athlete_id: "", event_code: "", mark: "", place: "", wind: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([api.get(`/track/athletes?organization_id=${orgId}`), api.get(`/track/results?meet_id=${meet.id}`)]);
      setAthletes(a.data); setResults(r.data);
    } catch { toast.error("Failed to load meet."); } finally { setLoading(false); }
  }, [orgId, meet.id]);
  useEffect(() => { load(); }, [load]);

  const addResult = async (e) => {
    e.preventDefault();
    if (!entry.athlete_id || !entry.event_code || !entry.mark) { toast.error("Athlete, event and mark are required."); return; }
    setSaving(true);
    try {
      await api.post("/track/results", { meet_id: meet.id, athlete_id: entry.athlete_id, event_code: entry.event_code, mark: entry.mark, place: entry.place ? Number(entry.place) : null, wind: entry.wind || null });
      toast.success("Result added."); setEntry({ ...entry, mark: "", place: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (r) => { try { await api.delete(`/track/results/${r.id}`); load(); } catch { toast.error("Delete failed."); } };

  return (
    <div data-testid="meet-detail">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-5 transition-colors"><ArrowLeft size={15} /> Back to meets</button>
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl">{meet.name}</h1>
      <p className="text-white/40 text-sm mt-1">{[meet.date, meet.location, meet.season].filter(Boolean).join(" · ") || "—"}</p>

      {canWrite && (
        <form onSubmit={addResult} className="bg-[#121212] border border-white/10 p-5 my-6 grid sm:grid-cols-5 gap-3 items-end" data-testid="result-entry-form">
          <div><label className={tLabel}>Athlete</label>
            <select data-testid="result-athlete" value={entry.athlete_id} onChange={(e) => setEntry({ ...entry, athlete_id: e.target.value })} className={tInput}>
              <option value="">—</option>{athletes.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div><label className={tLabel}>Event</label>
            <select data-testid="result-event" value={entry.event_code} onChange={(e) => setEntry({ ...entry, event_code: e.target.value })} className={tInput}>
              <option value="">—</option>{events.map((ev) => <option key={ev.code} value={ev.code}>{ev.name}</option>)}
            </select>
          </div>
          <div><label className={tLabel}>Mark / Time</label><input data-testid="result-mark" value={entry.mark} onChange={(e) => setEntry({ ...entry, mark: e.target.value })} placeholder="10.85 or 18-06.50" className={tInput} /></div>
          <div><label className={tLabel}>Place</label><input data-testid="result-place" value={entry.place} onChange={(e) => setEntry({ ...entry, place: e.target.value })} className={tInput} /></div>
          <button data-testid="result-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add</button>
        </form>
      )}

      {loading ? <div className="flex justify-center py-12 text-white/40"><Loader2 className="animate-spin" /></div>
        : results.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm mt-6" data-testid="meet-no-results">No results recorded for this meet yet.</div>
        : (
          <div className="border border-white/10 bg-[#121212] overflow-x-auto mt-6" data-testid="meet-results">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-white/10 text-white/40">{["Athlete", "Event", "Mark", "Place", ""].map((h, i) => <th key={i} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{h}</th>)}</tr></thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.id} data-testid={`result-${r.id}`} className="border-b border-white/5">
                    <td className="px-4 py-3 font-bold">{r.athlete_name}</td>
                    <td className="px-4 py-3 text-white/60">{r.event_code}</td>
                    <td className="px-4 py-3 font-mono-accent">{r.display}</td>
                    <td className="px-4 py-3 text-white/60">{r.place || "—"}</td>
                    <td className="px-4 py-3 text-right">{canWrite && <button data-testid={`del-result-${r.id}`} onClick={() => del(r)} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}
